/**
 * ProfessionalPdf.jsx
 * ---------------------------------------------------------------------------
 * A reusable, professional PDF document system built on @react-pdf/renderer.
 *
 * Provides:
 *   • <ProfessionalPdfDocument>  — the top-level <Document> wrapper. Pass
 *                                  metadata (title, author, …) and pages as
 *                                  children, or pass `sections` and let the
 *                                  component build the page for you.
 *
 *   • <ProfessionalPdfPage>      — an A4 page with a repeating Header at the
 *                                  top and a Footer (Powered-by + page number)
 *                                  at the bottom on every page.
 *
 *   • <PdfHeader>                — Logo (left), Document Title (center),
 *                                  Company address (right).  Reads from the
 *                                  `company` prop or from MasterCompanyDetails.
 *
 *   • <PdfFooter>                — "Powered by …" on the left, "Page X of Y"
 *                                  on the right, with a thin separator line.
 *
 *   • <PdfSection>               — Titled bordered block.
 *   • <PdfTable>                 — Header row + striped body + optional totals.
 *   • <PdfKeyValueGrid>          — Two-column key/value grid for forms.
 *
 *   • generatePdfBlob(doc)       — Helper that turns a Document into a Blob.
 *   • downloadPdf(doc, filename) — Convenience helper that triggers download.
 *
 * Usage (page-side):
 * ---------------------------------------------------------------------------
 *   import {
 *     ProfessionalPdfDocument, ProfessionalPdfPage,
 *     PdfSection, PdfTable, PdfKeyValueGrid, downloadPdf,
 *   } from "@/components/PrintWrapper/ProfessionalPdf";
 *
 *   const buildDoc = () => (
 *     <ProfessionalPdfDocument title="Rate Quotation" author={company.name}>
 *       <ProfessionalPdfPage title="RATE QUOTATION" company={company}>
 *         <PdfKeyValueGrid items={[
 *           { label: "Customer",   value: data.customerName },
 *           { label: "Quote #",    value: data.quoteNumber },
 *           …
 *         ]} />
 *         <PdfSection title="Charges">
 *           <PdfTable
 *             columns={[
 *               { key: "description", title: "Description", flex: 3 },
 *               { key: "qty",         title: "Qty",         flex: 1, align: "right" },
 *               { key: "rate",        title: "Rate",        flex: 1, align: "right" },
 *               { key: "amount",      title: "Amount",      flex: 1, align: "right" },
 *             ]}
 *             rows={data.charges}
 *             totals={[{ label: "Total", value: data.total }]}
 *           />
 *         </PdfSection>
 *       </ProfessionalPdfPage>
 *     </ProfessionalPdfDocument>
 *   );
 *
 *   await downloadPdf(buildDoc(), `RateQuote_${data.id}.pdf`);
 * ---------------------------------------------------------------------------
 */

import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  Font,
  pdf,
} from "@react-pdf/renderer";

/* ─────────────────────────────────────────────────────────────────────────────
 * Theme tokens — kept here so every page renders in a consistent design system.
 * Override individual tokens via the `theme` prop on <ProfessionalPdfDocument>.
 * ──────────────────────────────────────────────────────────────────────────── */
export const defaultTheme = {
  colors: {
    text:           "#111827",   // gray-900
    textMuted:      "#4b5563",   // gray-600
    textSubtle:     "#6b7280",   // gray-500
    border:         "#d1d5db",   // gray-300
    borderStrong:   "#000000",
    borderSoft:     "#e5e7eb",   // gray-200
    surface:        "#ffffff",
    surfaceAlt:     "#f9fafb",   // gray-50
    headerBg:       "#1e293b",   // slate-800
    headerText:     "#ffffff",
    sectionHeadBg:  "#f3f4f6",   // gray-100
    accent:         "#1a2744",   // brand navy
  },
  font: {
    family:     "Helvetica",
    familyBold: "Helvetica-Bold",
    sizeXs:     8,
    sizeSm:     9,
    sizeBase:   10,
    sizeMd:     11,
    sizeLg:     13,
    sizeXl:     16,
    sizeTitle:  18,
  },
  spacing: {
    xs: 2, sm: 4, md: 6, lg: 10, xl: 14, xxl: 20,
  },
  page: {
    paddingTop:    100,  // room for header
    paddingBottom: 50,   // room for footer
    paddingLeft:   28,
    paddingRight:  28,
  },
};

const mergeTheme = (override) => {
  if (!override) return defaultTheme;
  return {
    ...defaultTheme,
    ...override,
    colors:  { ...defaultTheme.colors,  ...(override.colors  || {}) },
    font:    { ...defaultTheme.font,    ...(override.font    || {}) },
    spacing: { ...defaultTheme.spacing, ...(override.spacing || {}) },
    page:    { ...defaultTheme.page,    ...(override.page    || {}) },
  };
};

/* ─────────────────────────────────────────────────────────────────────────────
 * Base stylesheet (built from theme tokens).
 * ──────────────────────────────────────────────────────────────────────────── */
const buildStyles = (t) =>
  StyleSheet.create({
    /* Page chrome ----------------------------------------------------------- */
    page: {
      paddingTop:    t.page.paddingTop,
      paddingBottom: t.page.paddingBottom,
      paddingLeft:   t.page.paddingLeft,
      paddingRight:  t.page.paddingRight,
      fontFamily:    t.font.family,
      fontSize:      t.font.sizeBase,
      color:         t.colors.text,
      backgroundColor: t.colors.surface,
      lineHeight:    1.4,
    },

    /* Header ---------------------------------------------------------------- */
    header: {
      position:  "absolute",
      top:       0,
      left:      0,
      right:     0,
      paddingTop:    20,
      paddingBottom: 12,
      paddingLeft:   t.page.paddingLeft,
      paddingRight:  t.page.paddingRight,
      borderBottomWidth: 1,
      borderBottomColor: t.colors.borderStrong,
      flexDirection: "row",
      alignItems:    "center",
      backgroundColor: t.colors.surface,
    },
    headerLogoWrap: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
    },
    headerLogo: {
      maxHeight: 38,
      maxWidth:  120,
      objectFit: "contain",
    },
    headerTitleWrap: {
      flex: 1.4,
      alignItems: "center",
    },
    headerTitle: {
      fontFamily: t.font.familyBold,
      fontSize:   t.font.sizeTitle,
      color:      t.colors.text,
      letterSpacing: 1,
    },
    headerAddrWrap: {
      flex: 1,
      alignItems: "flex-end",
    },
    headerCompanyName: {
      fontFamily: t.font.familyBold,
      fontSize:   t.font.sizeSm,
      color:      t.colors.text,
      marginBottom: 1,
    },
    headerAddrLine: {
      fontSize: t.font.sizeXs,
      color:    t.colors.textMuted,
      lineHeight: 1.35,
    },

    /* Footer ---------------------------------------------------------------- */
    footer: {
      position:  "absolute",
      bottom:    0,
      left:      0,
      right:     0,
      paddingTop:    8,
      paddingBottom: 16,
      paddingLeft:   t.page.paddingLeft,
      paddingRight:  t.page.paddingRight,
      borderTopWidth: 1,
      borderTopColor: t.colors.borderSoft,
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    footerLeft: {
      fontSize: t.font.sizeXs,
      color:    t.colors.textSubtle,
      fontFamily: t.font.familyBold,
    },
    footerRight: {
      fontSize: t.font.sizeXs,
      color:    t.colors.textSubtle,
    },

    /* Section --------------------------------------------------------------- */
    section: {
      marginBottom: t.spacing.lg,
      borderWidth:  1,
      borderColor:  t.colors.border,
      borderRadius: 2,
      overflow:     "hidden",
    },
    sectionHead: {
      backgroundColor: t.colors.sectionHeadBg,
      paddingVertical:   4,
      paddingHorizontal: 8,
      borderBottomWidth: 1,
      borderBottomColor: t.colors.border,
    },
    sectionHeadTitle: {
      fontFamily: t.font.familyBold,
      fontSize:   t.font.sizeSm,
      color:      t.colors.text,
      textTransform: "uppercase",
      letterSpacing: 0.6,
    },
    sectionBody: {
      padding: 8,
    },

    /* KeyValue grid --------------------------------------------------------- */
    kvGrid: {
      flexDirection: "row",
      flexWrap:      "wrap",
    },
    kvCell: {
      flexDirection: "row",
      paddingVertical:   3,
      paddingHorizontal: 4,
    },
    kvLabel: {
      fontFamily: t.font.familyBold,
      fontSize:   t.font.sizeSm,
      color:      t.colors.textMuted,
      width:      90,
    },
    kvValue: {
      flex: 1,
      fontSize: t.font.sizeSm,
      color:    t.colors.text,
    },

    /* Table ----------------------------------------------------------------- */
    table: {
      width: "100%",
      borderWidth: 1,
      borderColor: t.colors.border,
      borderRadius: 2,
      overflow: "hidden",
    },
    tableHeadRow: {
      flexDirection: "row",
      backgroundColor: t.colors.headerBg,
    },
    tableHeadCell: {
      fontFamily: t.font.familyBold,
      fontSize:   t.font.sizeXs,
      color:      t.colors.headerText,
      paddingVertical:   5,
      paddingHorizontal: 6,
      textTransform: "uppercase",
      letterSpacing: 0.4,
    },
    tableRow: {
      flexDirection: "row",
      borderTopWidth: 1,
      borderTopColor: t.colors.borderSoft,
    },
    tableRowAlt: {
      backgroundColor: t.colors.surfaceAlt,
    },
    tableCell: {
      fontSize: t.font.sizeSm,
      color:    t.colors.text,
      paddingVertical:   4,
      paddingHorizontal: 6,
    },
    tableFootRow: {
      flexDirection: "row",
      borderTopWidth: 1,
      borderTopColor: t.colors.borderStrong,
      backgroundColor: t.colors.surfaceAlt,
    },
    tableFootCell: {
      fontFamily: t.font.familyBold,
      fontSize:   t.font.sizeSm,
      color:      t.colors.text,
      paddingVertical:   5,
      paddingHorizontal: 6,
    },

    /* Misc ------------------------------------------------------------------ */
    paragraph: {
      fontSize: t.font.sizeSm,
      color: t.colors.text,
      marginBottom: t.spacing.sm,
    },
    note: {
      fontSize: t.font.sizeXs,
      color: t.colors.textSubtle,
      fontStyle: "italic",
    },
    spacer: { height: t.spacing.md },
  });

/* ─────────────────────────────────────────────────────────────────────────────
 * Header
 * ──────────────────────────────────────────────────────────────────────────── */
export function PdfHeader({ title, company, theme, styles }) {
  const t = theme || defaultTheme;
  const s = styles || buildStyles(t);

  const logo    = company?.logo;
  const name    = company?.name;
  // Address can come as either a comma-separated string or an array of lines.
  const addressLines = Array.isArray(company?.address)
    ? company.address.filter(Boolean)
    : (company?.address || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);

  return (
    <View style={s.header} fixed>
      <View style={s.headerLogoWrap}>
        {logo ? (
          <Image src={logo} style={s.headerLogo} />
        ) : null}
      </View>

      <View style={s.headerTitleWrap}>
        {title ? <Text style={s.headerTitle}>{title}</Text> : null}
      </View>

      <View style={s.headerAddrWrap}>
        {name ? <Text style={s.headerCompanyName}>{name}</Text> : null}
        {addressLines.map((line, i) => (
          <Text key={i} style={s.headerAddrLine}>{line}</Text>
        ))}
      </View>
    </View>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Footer (Powered by … + page numbers, repeats on every page)
 * ──────────────────────────────────────────────────────────────────────────── */
export function PdfFooter({ poweredBy, theme, styles, showPageNumbers = true }) {
  const t = theme || defaultTheme;
  const s = styles || buildStyles(t);

  return (
    <View style={s.footer} fixed>
      <Text style={s.footerLeft}>
        {poweredBy ? `Powered by ${poweredBy}` : ""}
      </Text>
      {showPageNumbers && (
        <Text
          style={s.footerRight}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          }
        />
      )}
    </View>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Section (titled bordered block)
 * ──────────────────────────────────────────────────────────────────────────── */
export function PdfSection({ title, children, theme, styles, wrap = true }) {
  const t = theme || defaultTheme;
  const s = styles || buildStyles(t);
  return (
    <View style={s.section} wrap={wrap}>
      {title ? (
        <View style={s.sectionHead}>
          <Text style={s.sectionHeadTitle}>{title}</Text>
        </View>
      ) : null}
      <View style={s.sectionBody}>{children}</View>
    </View>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Two-column key/value grid
 *
 * items: [{ label, value, width? }]
 *   width: fraction of row width — "100%", "50%", "33%" etc. Default "50%".
 * ──────────────────────────────────────────────────────────────────────────── */
export function PdfKeyValueGrid({ items = [], theme, styles }) {
  const t = theme || defaultTheme;
  const s = styles || buildStyles(t);
  return (
    <View style={s.kvGrid}>
      {items.map((it, i) => (
        <View
          key={i}
          style={[s.kvCell, { width: it.width || "50%" }]}
        >
          <Text style={s.kvLabel}>{it.label}</Text>
          <Text style={s.kvValue}>{it.value ?? "-"}</Text>
        </View>
      ))}
    </View>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Table
 *
 * columns: [{ key, title, flex?, width?, align? }]
 *    flex   – flexbox ratio (default 1)
 *    width  – fixed width (overrides flex)
 *    align  – "left" | "center" | "right" (default left)
 *    render – (row, rowIndex) => ReactNode | string  (optional custom renderer)
 *
 * rows: array of objects keyed by `columns[].key`.
 *
 * totals: [{ label, value, colSpan? }] — optional totals footer.
 * ──────────────────────────────────────────────────────────────────────────── */
export function PdfTable({
  columns = [],
  rows = [],
  totals = null,
  striped = true,
  theme,
  styles,
}) {
  const t = theme || defaultTheme;
  const s = styles || buildStyles(t);

  const cellStyle = (col, base) => [
    base,
    col.width
      ? { width: col.width }
      : { flex: col.flex || 1 },
    { textAlign: col.align || "left" },
  ];

  return (
    <View style={s.table}>
      {/* Head */}
      <View style={s.tableHeadRow} fixed>
        {columns.map((col) => (
          <Text key={col.key} style={cellStyle(col, s.tableHeadCell)}>
            {col.title}
          </Text>
        ))}
      </View>

      {/* Body */}
      {rows.map((row, ri) => {
        const isAlt = striped && ri % 2 === 1;
        return (
          <View
            key={row.id ?? row.rowid ?? ri}
            style={[s.tableRow, isAlt && s.tableRowAlt]}
            wrap={false}
          >
            {columns.map((col) => {
              const raw = col.render
                ? col.render(row, ri)
                : row[col.key];
              const text =
                raw === null || raw === undefined ? "" : String(raw);
              return (
                <Text key={col.key} style={cellStyle(col, s.tableCell)}>
                  {text}
                </Text>
              );
            })}
          </View>
        );
      })}

      {/* Empty state */}
      {rows.length === 0 && (
        <View style={s.tableRow}>
          <Text
            style={[
              s.tableCell,
              { flex: 1, textAlign: "center", color: t.colors.textSubtle },
            ]}
          >
            No records.
          </Text>
        </View>
      )}

      {/* Totals */}
      {Array.isArray(totals) && totals.length > 0 && (
        <View style={s.tableFootRow}>
          {totals.map((tot, i) => (
            <Text
              key={i}
              style={[
                s.tableFootCell,
                { flex: tot.colSpan || 1, textAlign: tot.align || "right" },
              ]}
            >
              {tot.label ? `${tot.label}: ` : ""}{tot.value ?? ""}
            </Text>
          ))}
        </View>
      )}
    </View>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * ProfessionalPdfPage
 *
 * An A4 page with the repeating header (top) + footer (bottom).  Drop any
 * pdf-aware children inside (View / Text / Image / our PdfSection, PdfTable …).
 * ──────────────────────────────────────────────────────────────────────────── */
export function ProfessionalPdfPage({
  title,
  company,
  poweredBy,
  showPageNumbers = true,
  size = "A4",
  orientation = "portrait",
  theme,
  children,
}) {
  const t = mergeTheme(theme);
  const s = buildStyles(t);

  return (
    <Page size={size} orientation={orientation} style={s.page} wrap>
      <PdfHeader title={title} company={company} theme={t} styles={s} />
      <View>{children}</View>
      <PdfFooter
        poweredBy={poweredBy ?? company?.poweredBy}
        showPageNumbers={showPageNumbers}
        theme={t}
        styles={s}
      />
    </Page>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * ProfessionalPdfDocument
 *
 * Top-level wrapper.  Accepts <ProfessionalPdfPage> children for multi-page
 * documents, or a single `page` / `sections` shortcut.
 * ──────────────────────────────────────────────────────────────────────────── */
export function ProfessionalPdfDocument({
  title,
  subject,
  author,
  keywords,
  language = "en",
  pdfVersion,
  producer = "Shipez AI",
  creator = "Shipez AI",
  children,
}) {
  return (
    <Document
      title={title}
      subject={subject}
      author={author}
      keywords={keywords}
      language={language}
      pdfVersion={pdfVersion}
      producer={producer}
      creator={creator}
    >
      {children}
    </Document>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Helpers — register a custom font (optional) and convert a Document to a Blob
 * / trigger a download.
 * ──────────────────────────────────────────────────────────────────────────── */
export const registerPdfFont = (family, sources) => {
  // sources: [{ src, fontWeight?, fontStyle? }] | { src }
  if (!family || !sources) return;
  if (Array.isArray(sources)) {
    Font.register({ family, fonts: sources });
  } else {
    Font.register({ family, src: sources.src });
  }
};

export const generatePdfBlob = async (docElement) =>
  await pdf(docElement).toBlob();

export const downloadPdf = async (docElement, filename = "document.pdf") => {
  const blob = await generatePdfBlob(docElement);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/* ─────────────────────────────────────────────────────────────────────────────
 * Hook — usePdfDownload
 *
 * Returns { downloading, downloadPdf }.  Wires up a loading flag so pages can
 * disable the download button while the PDF is being generated.
 *
 *   const { downloading, downloadPdf } = usePdfDownload();
 *   <button disabled={downloading}
 *           onClick={() => downloadPdf(buildDoc(), "RateQuote.pdf")} />
 * ──────────────────────────────────────────────────────────────────────────── */
export function usePdfDownload() {
  const [downloading, setDownloading] = React.useState(false);

  const trigger = React.useCallback(async (docElement, filename) => {
    try {
      setDownloading(true);
      await downloadPdf(docElement, filename);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[ProfessionalPdf] download failed:", err);
      throw err;
    } finally {
      setDownloading(false);
    }
  }, []);

  return { downloading, downloadPdf: trigger };
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Convenience — read company info from localStorage in one call, so pages do
 * not have to repeat the parsing logic.
 * ──────────────────────────────────────────────────────────────────────────── */
export const getCompanyFromMasterDetails = () => {
  try {
    const raw = localStorage.getItem("MasterCompanyDetails");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const m = Array.isArray(parsed) ? parsed[0] : parsed;
    if (!m) return null;
    return {
      name:      m.CompanyName,
      logo:      m.CompanyLogoDark || m.CompanyLogo || null,
      address:   m.ProfileAddressPrint || m.CompanyAddress || "",
      poweredBy: m.PoweredBy || "",
      email:     m.CompanyEmail,
      phone:     m.CompanyPhone,
      website:   m.CompanyWebsite,
    };
  } catch {
    return null;
  }
};

export default ProfessionalPdfDocument;
